import { ref, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'
import { debounce } from 'lodash-es'

interface WatermarkConfig {
  /** 防御（默认开启，能防御水印被删除或隐藏，但可能会有性能损耗） */
  defense: boolean
  /** 文本颜色 */
  color: string
  /** 文本透明度 */
  opacity: number
  /** 文本字体大小 */
  size: number
  /** 文本字体 */
  family: string
  /** 文本倾斜角度 */
  angle: number
  /** 一处水印所占宽度（数值越大水印密度越低） */
  width: number
  /** 一处水印所占高度（数值越大水印密度越低） */
  height: number
}

interface WatermarkObservers {
  watermarkElMutationObserver?: MutationObserver
  parentElMutationObserver?: MutationObserver
  parentElResizeObserver?: ResizeObserver
}

// 默认配置
const DEFAULT_CONFIG: WatermarkConfig = {
  defense: true,
  color: '#c0c4cc',
  opacity: 0.5,
  size: 16,
  family: '黑体',
  angle: -20,
  width: 300,
  height: 200,
}

// 默认挂载点为body
const defaultMountPoint = ref(document.body)

interface WatermarkInstance {
  setWatermark: (text: string, config?: Partial<WatermarkConfig>) => void
  clearWatermark: () => void
}

/**
 * 创建水印实例
 * @param mountPoint - 挂载点，默认为document.body
 * @returns WatermarkInstance
 */
function createWatermark(mountPoint: Ref<HTMLElement | null> = defaultMountPoint): WatermarkInstance {
  let watermarkText = ''
  let watermarkConfig: WatermarkConfig = { ...DEFAULT_CONFIG }
  let watermarkElement: HTMLElement | null = null

  // 存储各种观察器
  const observers: WatermarkObservers = {
    watermarkElMutationObserver: undefined,
    parentElMutationObserver: undefined,
    parentElResizeObserver: undefined,
  }

  /**
   * 设置水印
   * @param text - 水印文本
   * @param config - 水印配置
   */
  function setWatermark(text: string, config: Partial<WatermarkConfig> = {}): void {
    if (!mountPoint.value) {
      return console.warn('请在 DOM 挂载完成后再调用 setWatermark 方法设置水印')
    }

    watermarkText = text
    watermarkConfig = { ...DEFAULT_CONFIG, ...config }

    if (watermarkElement) {
      updateWatermarkStyle()
    } else {
      createWatermarkElement()
    }

    setupObservers(mountPoint.value)
  }

  /**
   * 创建水印元素
   */
  function createWatermarkElement(): void {
    const isBody = mountPoint.value!.tagName.toLowerCase() === defaultMountPoint.value.tagName.toLowerCase()
    const position = isBody ? 'fixed' : 'absolute'
    const parentPosition = isBody ? '' : 'relative'

    watermarkElement = document.createElement('div')
    watermarkElement.style.pointerEvents = 'none'
    watermarkElement.style.top = '0'
    watermarkElement.style.left = '0'
    watermarkElement.style.position = position
    watermarkElement.style.zIndex = '99999'

    const { clientWidth, clientHeight } = mountPoint.value!
    updateWatermarkStyle({ width: clientWidth, height: clientHeight })

    mountPoint.value!.style.position = parentPosition
    mountPoint.value!.appendChild(watermarkElement)
  }

  /**
   * 更新水印样式
   * @param options - 可选的样式参数
   */
  function updateWatermarkStyle(options: { width?: number; height?: number } = {}): void {
    if (!watermarkElement) return

    if (watermarkText) {
      watermarkElement.style.background = `url(${generateWatermarkImage()}) left top repeat`
    }

    if (options.width) {
      watermarkElement.style.width = `${options.width}px`
    }

    if (options.height) {
      watermarkElement.style.height = `${options.height}px`
    }
  }

  /**
   * 生成水印图片
   * @returns 水印图片的dataURL
   */
  function generateWatermarkImage(): string {
    const { color, opacity, size, family, angle, width, height } = watermarkConfig
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) return ''

    ctx.fillStyle = color
    ctx.globalAlpha = opacity
    ctx.font = `${size}px ${family}`
    ctx.rotate((Math.PI / 180) * angle)
    ctx.fillText(watermarkText, 0, height / 2)

    return canvas.toDataURL()
  }

  /**
   * 清除水印
   */
  function clearWatermark(): void {
    if (!mountPoint.value || !watermarkElement) return

    destroyObservers()

    try {
      mountPoint.value.removeChild(watermarkElement)
    } catch {
      console.warn('水印元素已不存在，请重新创建')
    } finally {
      watermarkElement = null
    }
  }

  // 防抖重建水印函数
  const debouncedRecreateWatermark = debounce(() => {
    clearWatermark()
    createWatermarkElement()
    if (mountPoint.value) {
      setupObservers(mountPoint.value)
    }
  }, 100)

  /**
   * 设置观察器
   * @param element - 要观察的元素
   */
  function setupObservers(element: HTMLElement): void {
    if (watermarkConfig.defense) {
      if (!observers.watermarkElMutationObserver && !observers.parentElMutationObserver) {
        setupMutationObservers(element)
      }
    } else {
      destroyObservers('mutation')
    }

    if (!observers.parentElResizeObserver) {
      setupResizeObserver(element)
    }
  }

  /**
   * 销毁观察器
   * @param type - 观察器类型 ('all', 'mutation', 'resize')
   */
  function destroyObservers(type: 'all' | 'mutation' | 'resize' = 'all'): void {
    if (type === 'mutation' || type === 'all') {
      if (observers.watermarkElMutationObserver) {
        observers.watermarkElMutationObserver.disconnect()
        observers.watermarkElMutationObserver = undefined
      }
      if (observers.parentElMutationObserver) {
        observers.parentElMutationObserver.disconnect()
        observers.parentElMutationObserver = undefined
      }
    }

    if (type === 'resize' || type === 'all') {
      if (observers.parentElResizeObserver) {
        observers.parentElResizeObserver.disconnect()
        observers.parentElResizeObserver = undefined
      }
    }
  }

  /**
   * 设置变异观察器
   * @param element - 要观察的元素
   */
  function setupMutationObservers(element: HTMLElement): void {
    const mutationCallback = debounce((mutationsList: MutationRecord[]) => {
      mutationsList.forEach(
        debounce((mutation: MutationRecord) => {
          switch (mutation.type) {
            case 'attributes':
              if (mutation.target === watermarkElement) {
                debouncedRecreateWatermark()
              }
              break
            case 'childList':
              mutation.removedNodes.forEach((node) => {
                if (node === watermarkElement) {
                  element.appendChild(watermarkElement!)
                }
              })
              break
          }
        }, 100)
      )
    }, 100)

    observers.watermarkElMutationObserver = new MutationObserver(mutationCallback)
    observers.parentElMutationObserver = new MutationObserver(mutationCallback)

    observers.watermarkElMutationObserver.observe(watermarkElement!, {
      attributes: true,
      childList: false,
      subtree: false,
    })

    observers.parentElMutationObserver.observe(element, {
      attributes: false,
      childList: true,
      subtree: false,
    })
  }

  /**
   * 设置大小变化观察器
   * @param element - 要观察的元素
   */
  function setupResizeObserver(element: HTMLElement): void {
    const resizeCallback = debounce(() => {
      const { clientWidth, clientHeight } = element
      updateWatermarkStyle({ width: clientWidth, height: clientHeight })
    }, 500)

    observers.parentElResizeObserver = new ResizeObserver(resizeCallback)
    observers.parentElResizeObserver.observe(element)
  }

  // 组件卸载时清除水印
  onBeforeUnmount(() => {
    clearWatermark()
  })

  return {
    setWatermark,
    clearWatermark,
  }
}

export { createWatermark as useWatermark }